define('services/callService',[
    'enums/participantStatus',
    'lib/bluebird',
    'lib/knockout',
    'lib/lodash4',
    'models/Call',
    'services/apiCore',
    'services/authService',
    'services/communicationService',
    'services/loggingService',
    'services/notificationService',
    'services/toastService',
    'services/userService',
    'services/embeddingService',
    'util/PubSub'
], function (
    participantStatus,
    Promise,
    ko,
    _,
    Call,
    apiCore,
    authService,
    communicationService,
    LOG,
    notificationService,
    toasts,
    userService,
    embeddingService,
    PubSub
) {
    'use strict';

    var activeCalls = ko.observableArray([]);

    var MAKE_CALL_NOTIFICATION_TOPIC     = 'callService.notifications.makeCall';

    /**
     * Call service. Handles call retrieval, updates & subscriptions.
     * @exports services/callService
     */
    var callService = {
        uriBuilder: communicationService.createUriBuilder('/conversations/calls'),

        // ===============================================================================
        // Properties
        // ===============================================================================

        /**
         * Observable array of all active calls
         */
        activeCalls: activeCalls,

        /**
         * Observable array of all active user calls. This is a subset of activeCalls.
         */
        activeUserCalls: ko.computed(function () {
            return _.filter(activeCalls(), function (call) {
                return call.isUserCall();
            });
        }),

        /**
         * Observable array of all active agent calls. This is a subset of activeCalls.
         */
        activeAgentCalls: ko.computed(function () {
            return _.filter(activeCalls(), function (call) {
                return call.isAgentCall();
            });
        }),

        // ===============================================================================
        // Methods
        // ===============================================================================

        getCall: function (callId, options) {
            var opts = _.assign({
                shouldReturnRawData: false
            }, options);

            return communicationService.getCommunication(this.uriBuilder, callId)
                        .then(function (callData) {
                            if (opts.shouldReturnRawData) {
                                return callData;
                            }

                            return new Call(callData);
                        });
        },

        /**
         * Get the calls for a current user
         * @return {Promise}
         */
        getActiveCalls: function() {
            return apiCore.v2.get(callService.uriBuilder.communicationsByType('call'));
        },

        /**
         * Monitor a participant on a call
         * @param {string} id the id of the call to monitor.
         * @param {string} participantId the id of the participant to monitor.
         * @param {Boolean} notifySubscribers indicates if subscribes to new answered calls should be notified
         *                  of the newly monitored call.
         *
         * @return {promise} a promise that resolves with the {@link Call} object
         */
        monitorCall: function (id, participantId, notifySubscribers) {
            var self = this;

            // This is a call that the current user is currently not a participant of.  So, we have to request
            // to be added as a monitor and then wait for the new call to show up in the active calls list, and then
            // verify that the current user's monitor attribute is set to true.
            return apiCore.v2.post(callService.uriBuilder.monitor(id, participantId), null, null)
                .then(function () {
                    return waitForNewCallToBeCreated(id);
                })
                .then(function (call) {
                    return communicationService.validateActionSucceeded(call.id,
                        call.participantForCurrentUser().id, Call, 'status', participantStatus.CONNECTED, self.activeCalls, self.uriBuilder);
                })
                .then(function (call) {
                    if (notifySubscribers) {
                        PubSub.Topic(MAKE_CALL_NOTIFICATION_TOPIC).publish(id);
                    }
                    return call;
                })
                .catch(function (err) {
                    LOG.error("Error while monitoring call:", err);
                    if (err.details && err.details.response && err.details.response.code) {
                        toasts.notifyMajorAlert('phone.monitorCall.error.' + err.details.response.code);
                    } else {
                        toasts.notifyMajorAlert('phone.monitorCall.error.generic');
                    }
                });
        },

        /**
         * Create a new call for the current user
         * @param {Object} options the call options object that can have the following properties:
         *              call - the phone number/address to call
         *              callUserId - the id of the user to call
         *              callQueueId - the id of the queue to call
         *              callFrom - the id of the queue to call on behalf of
         * @param {Boolean} notifySubscribers indicates if subscribes to new answered calls should be notified
         *                  of the newly monitored call.
         * @return {promise} a promise that resolves with the {@link Call} object
         */
        createCall: function (options, notifySubscribers) {
            return apiCore.v2.post(callService.uriBuilder.base(), null, options)
                .then(function (callData) {
                    return waitForNewCallToBeCreated(callData.id)
                        .then(function (call) {
                            if (notifySubscribers) {
                                PubSub.Topic(MAKE_CALL_NOTIFICATION_TOPIC).publish(call.id);
                            }
                            return call;
                        });
                });
        },

        /**
         * Creates a new call from the current user to the
         * provided destination address.
         * @param {string} destAddress the telephony address to call
         * @param {Boolean} notifySubscribers indicates if subscribes to new answered calls should be notified
         *                  of the newly monitored call.
         * @return {promise} a promise that resolves with the {@link Call} object
         */
        makeCall : function (destAddress, notifySubscribers) {
            return callService.createCall({ call: destAddress }, notifySubscribers);
        },

        /**
         * Ends an ongoing call.
         * @param {string} callId the id of the call to end.
         * @param {string} participant the id of the participent to disconnect from the call.
         * @return {promise} a promise that resolves with the {@link Call} object
         */
        endCall : function(id, participantId) {
            return communicationService.disconnect(this.uriBuilder, id, participantId, this.activeCalls, Call);
        },

        forceTransfer: function (callId, participantId, options) {
            return apiCore.v2.post(callService.uriBuilder.replace(callId, participantId), options);
        },

        saveAfterCallWork: function (callId, participantId, data) {
            return apiCore.v2.patch(this.uriBuilder.participant(callId, participantId), data);
        }
    };

    communicationService.subscribeToTopic('conversations.calls', callService.activeCalls, callService.getActiveCalls, Call);

    /**
     * Handler for  new calls that are added to a user's live
     * call data.
     */
    var onNewUserCall = function (call) {
        LOG.debug('Call state tracked in active calls', call.id);
    };

    /**
     * Pushes call data into active calls, replacing any missing data,
     * but never overwriting existing data from push events. This
     * behavior ensures eventual consistency, provided that the
     * callData is fetched after activeCalls becomes live.
     *
     * @param {object} callData raw call data to push to active calls
     */
    var fillToActiveCalls = function (callData) {
        var
            activeCalls = callService.activeCalls,
            call = _.find(activeCalls(), {id: callData.id});
        if (call) {
            LOG.debug('Filling data into active calls', callData);
            Call.fill(call, callData);
        } else {
            LOG.debug('Adding call to activeCalls', callData);
            call = addToActiveCalls(callData);
        }
        return call;
    };

    /**
     * Adds a new call object to the list of active calls based on
     * the provided call data, and sets up tracking so the call
     * will be remove when the user disconnects.
     * @param {object} callData raw call data to add to active calls
     */
    var addToActiveCalls = function (callData) {
        var call = new Call(callData);
        callService.activeCalls.push(call);
        onNewUserCall(call);
        return call;
    };

    var waitForNewCallToBeCreated = function (callId) {
        var promiseResolver = Promise.defer();

        var call = _.find(activeCalls(), {id: callId});

        if (call) {
            //if everything got fixed up while we were doing other stuff, we're done here.
            promiseResolver.resolve(call);
            return promiseResolver.promise;
        }

        var pollingObj = {
            timeout: null,
            xhrRequest: null
        };

        var subscription = callService.activeCalls.subscribe(function () {
            var call = _.find(activeCalls(), {id: callId});

            if (call) {
                promiseResolver.resolve(call);

                if (pollingObj.timeout) {
                    clearTimeout(pollingObj.timeout);
                }

                if (pollingObj.xhrRequest) {
                    pollingObj.xhrRequest.cancel();
                }

                subscription.dispose();
            }
        });

        var tries = 0;
        var callIsDisconnected = false;

        var getCallTimeout = function () {
            pollingObj.xhrRequest = callService.getCall(callId, {
                shouldReturnRawData: true
            });

            pollingObj.xhrRequest
                .then(function (callData) {
                    if (_.has(callData, "participants")) {
                        if (subscription) {
                            // get rid of the subscription on success becuase we
                            // just got the latest data from the backend.
                            subscription.dispose();
                        }

                        var call = new Call(callData);
                        // only add in the call if it is active.
                        if (!call.isDisconnected()) {
                            call = fillToActiveCalls(callData);
                        }
                        callIsDisconnected = call.isDisconnected();
                        promiseResolver.resolve(call);
                    }
                })
                .catch(Promise.CancellationError, _.noop)
                .finally(function () {
                    var call = _.find(activeCalls(), {id: callId});

                    // if the call is found or is not active then don't try anymore
                    if (call || callIsDisconnected) {
                        if (subscription) { subscription.dispose(); }
                        pollingObj.xhrRequest = null;
                    } else {
                        if (tries > 15) {
                            // stop trying to get the call's data after 15 seconds as there
                            // was most likely an error in creating the call. This number
                            // came from Anthony Roach.
                            if (subscription) { subscription.dispose(); }
                            promiseResolver.reject();
                        } else {
                            // The call has not been fully created yet, so try again
                            pollingObj.timeout = setTimeout(getCallTimeout, 1000);
                            tries++;
                        }
                    }
                });
        };

        // Poll for the new call after waiting for 15s
        pollingObj.timeout = setTimeout(getCallTimeout, 1000);

        return promiseResolver.promise;
    };

    ko.computed(function () {
        callService.activeCalls(_.filter(callService.activeCalls(), function (call) {
            if (_.has(call, 'active')) {
                return call.active();
            }

            return false;
        }));
    });

    return callService;
});

